/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { InvertFillButton } from '../InvertFillButton';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Row,
  Select,
  Table,
  Typography,
  Upload,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined, InboxOutlined } from '@ant-design/icons';
import { CheckCircle2, Clock, CreditCard, FileText, XCircle } from 'lucide-react';
import moment from 'moment';

import callApi, { uploadImageApiCall } from '../../Api/config';
import { PAYMENT_VOUCHER } from '../../constants/EntityName';
import { Role } from '../../constants/AppConstant';
import {
  FEE_TYPES,
  RECEIPT_SOURCES,
  RECORD_PAYMENT_FEE_TYPES,
  COURIER_PORTAL_OPTIONS,
  VOUCHER_PAYMENT_METHODS,
  WITHDRAW_PAYMENT_MODES,
  isCourierFeeType,
  isRefundFeeType,
  resolveOrderPaymentMethod,
} from '../../constants/paymentVoucher';
import { getAdminName, getRole } from '../../service/CookieManager';
import { toastError, toastSuccess } from '../../service/Toast';
import {
  displayDate,
  displayText,
  downloadVoucherReport,
  money,
  statusTag,
  withdrawalColumns,
} from './helpers';
import '../style.scss';

const { Panel } = Collapse;
const { Text } = Typography;
const { Dragger } = Upload;

const toSelectOptions = (options: string[]) =>
  options.map((option: string) => ({ label: option, value: option }));

const currencySymbol = (code?: string) => {
  if (code === 'INR') return '₹';
  if (code === 'USD') return '$';
  return code || '';
};

const renderPaymentMethodLabel = (method: string) => {
  const methodCurrency = (VOUCHER_PAYMENT_METHODS as any)[method]?.currency;
  const symbol = currencySymbol(methodCurrency);
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        gap: 16,
      }}
    >
      <span>{method}</span>
      <span style={{ fontWeight: 600, color: '#595959', marginLeft: 'auto' }}>{symbol}</span>
    </div>
  );
};

const selectPopupContainer = (trigger: HTMLElement) => trigger.parentElement || document.body;

const disableFutureDates = (current: any) => !!current && current > moment().endOf('day');

const compactLabelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap' as const,
};

const compactFormItemStyle = { marginBottom: 12 };

const normFile = (e: any) => {
  if (Array.isArray(e)) return e;
  return e?.fileList || [];
};

const getUploadFile = (fileList: any[]): File | null => {
  const file = fileList?.[0];
  return file?.originFileObj || file || null;
};

const useUploadImagePreview = (fileList: any[] = []) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const file = fileList[0];
    if (!file) {
      setPreviewUrl('');
      return;
    }
    if (file.url || file.thumbUrl) {
      setPreviewUrl(file.url || file.thumbUrl);
      return;
    }
    if (file.originFileObj) {
      const objectUrl = URL.createObjectURL(file.originFileObj);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setPreviewUrl('');
  }, [fileList]);

  return previewUrl;
};

const EMPTY_FILE_LIST: any[] = [];

const normalizeOrderMembers = (members: any[] = []) =>
  (members || [])
    .filter((member: any) => member?.id != null)
    .map((member: any) => ({
      id: String(member.id),
      name: member.name || `Member ${member.id}`,
    }));

const mergeVoucherMembers = (voucher: any, orderMembers: any[] = []) => {
  const fromVoucher = normalizeOrderMembers(voucher?.members || []);
  if (fromVoucher.length) {
    return { ...voucher, members: fromVoucher };
  }
  return { ...voucher, members: normalizeOrderMembers(orderMembers) };
};

const getVoucherKey = (voucher: any): string | null => {
  const key = voucher?.id ?? voucher?.voucherId;
  if (key === undefined || key === null || key === '' || key === 'undefined') {
    return null;
  }
  return String(key);
};

const isValidVoucher = (voucher: any) => Boolean(getVoucherKey(voucher));

const toOptionalString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const normalized = String(value).trim();
  return normalized || undefined;
};

const appendReceiptFields = (
  formData: FormData,
  line: {
    paymentMethod: string;
    bankAccount?: string;
    receivedAmount: number;
    receivedDate: string;
    receiptSource: string;
    feeCategory?: string;
    senderName?: string | number;
    transactionId?: string | number;
  }
) => {
  formData.append('paymentMethod', line.paymentMethod);
  if (line.paymentMethod === 'Bank Transfer' && line.bankAccount) {
    formData.append('bankAccount', line.bankAccount);
  }
  formData.append('receivedAmount', String(line.receivedAmount));
  formData.append('receivedDate', line.receivedDate);
  formData.append('receiptSource', line.receiptSource);
  if (line.feeCategory) {
    formData.append('feeCategory', line.feeCategory);
  }
  const senderName = toOptionalString(line.senderName);
  if (senderName) {
    formData.append('senderName', senderName);
  }
  const transactionId = toOptionalString(line.transactionId);
  if (transactionId) {
    formData.append('transactionId', transactionId);
  }
};

const isDocumitraReceiptSource = (source?: string | null) =>
  String(source || '').trim() === 'From Documitra Fee';

/** Documitra Fee may be recorded once per voucher (govt fee is unlimited). */
const voucherHasDocumitraFeeReceipt = (voucher: any) =>
  (voucher?.receipts || []).some((row: any) => isDocumitraReceiptSource(row?.receiptSource));

/**
 * Courier Label / Apostille label amounts on Documitra Fee receipts (non-editable):
 * $40 per unit; Courier Label uses the order's courier price per member when saved.
 */
const ADDON_FEE_PER_UNIT_USD = 40;

const roundUsd = (value: number) => Math.round(value * 100) / 100;

const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const orderMemberCount = (formDetails?: any) =>
  Math.max(1, Array.isArray(formDetails?.members) ? formDetails.members.length : 0);

const addonFee = (count: number, unit: string, unitPrice = ADDON_FEE_PER_UNIT_USD) => ({
  amount: roundUsd(unitPrice * count),
  note: `${pluralize(count, unit)} × $${unitPrice}`,
});

/**
 * Courier Label: the order's courier price per member × members ($40, or $80
 * on Combo 1: Renunciation & OCI).
 */
const orderCourierLabelFee = (formDetails?: any) => {
  const pricePerMember = Number(formDetails?.courierService?.price);

  return addonFee(
    orderMemberCount(formDetails),
    'member',
    pricePerMember > 0 ? pricePerMember : ADDON_FEE_PER_UNIT_USD
  );
};

/** Apostille Fee: the label fee only, $40 × documents (members when the order stored none). */
const orderApostilleFee = (formDetails?: any) =>
  addonFee(Number(formDetails?.apostilled?.quantity) || orderMemberCount(formDetails), 'document');

const orderHasCourierAddon = (formDetails?: any) => Boolean(formDetails?.courierService?.selected);
/**
 * The order's Apostille includes a label: the optional label fee with the
 * Apostille option, or New York (label included in its price). Orders from
 * before these options (no option saved) are treated as before.
 */
const orderHasApostilleLabelFee = (formDetails?: any) => {
  const apostilled = formDetails?.apostilled;
  if (!apostilled?.selected) return false;
  if (!apostilled.option) return true;

  return apostilled.option === 'NEW_YORK' || Boolean(apostilled.labelFee);
};
const orderHasAdditionalFeeAddons = (formDetails?: any) =>
  orderHasCourierAddon(formDetails) || orderHasApostilleLabelFee(formDetails);

const DocumitraFeeSectionFields = ({
  sectionKey,
  title,
  form,
  lockedMethod,
  methodEditable,
  amountEditable = true,
  amountNote,
  hidePaymentFields = false,
  allowedMethods,
}: {
  sectionKey: string;
  title: string;
  form: any;
  lockedMethod?: string;
  methodEditable: boolean;
  /** When false, amount is locked (e.g. Courier Label, calculated from members). */
  amountEditable?: boolean;
  /** How the amount was calculated, shown next to the label. */
  amountNote?: string;
  hidePaymentFields?: boolean;
  /** When set (e.g. existing voucher currency), only these methods are shown. */
  allowedMethods?: string[];
}) => {
  const methodPath = [sectionKey, 'paymentMethod'];
  const paymentMethod = Form.useWatch(methodPath, form);
  const methodConfig = paymentMethod ? (VOUCHER_PAYMENT_METHODS as any)[paymentMethod] : null;
  const currency = methodConfig?.currency || 'USD';
  const methodChoices =
    allowedMethods && allowedMethods.length ? allowedMethods : Object.keys(VOUCHER_PAYMENT_METHODS);
  const amountField = (
    <Form.Item
      name={[sectionKey, 'receivedAmount']}
      label={
        <span style={compactLabelStyle}>
          {title}
          {(amountNote || !amountEditable) && (
            <span style={{ fontWeight: 400 }}>
              {' '}
              ({[amountNote, !amountEditable ? 'non-editable' : null].filter(Boolean).join(', ')})
            </span>
          )}
        </span>
      }
      style={{ ...compactFormItemStyle, padding: 0 }}
    >
      <InputNumber<number>
        key={`${sectionKey}-${currency}`}
        style={{ width: '100%' }}
        min={0}
        precision={2}
        controls={false}
        disabled={!amountEditable}
        readOnly={!amountEditable}
        placeholder="0.00"
        formatter={value =>
          value === undefined || value === null ? '' : `${currencySymbol(currency)} ${value}`
        }
        parser={value => Number(value?.replace(/[^\d.]/g, '') || 0)}
      />
    </Form.Item>
  );

  if (hidePaymentFields) {
    return (
      <Col xs={24} md={8}>
        {amountField}
      </Col>
    );
  }

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 4 }}>
      <Col xs={24} md={8}>
        {amountField}
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          name={[sectionKey, 'paymentDate']}
          label={<span style={compactLabelStyle}>Payment Date</span>}
          style={{ ...compactFormItemStyle, padding: 0 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD-MM-YYYY"
            disabledDate={disableFutureDates}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          name={[sectionKey, 'paymentMethod']}
          label={
            <span style={compactLabelStyle}>
              Payment Method [{currencySymbol(currency)} / {currency}]
            </span>
          }
          style={{ ...compactFormItemStyle, padding: 0 }}
        >
          <Select
            allowClear={methodEditable}
            disabled={!methodEditable}
            placeholder="Payment method"
            dropdownClassName="select-checkbox-dropdown"
            options={methodChoices.map(method => ({
              label: `${method} (${(VOUCHER_PAYMENT_METHODS as any)[method]?.currency || 'USD'})`,
              value: method,
            }))}
            getPopupContainer={selectPopupContainer}
            onChange={() =>
              form.setFieldsValue({
                [sectionKey]: {
                  ...(form.getFieldValue(sectionKey) || {}),
                  bankAccount: undefined,
                },
              })
            }
          />
        </Form.Item>
        {!methodEditable && lockedMethod && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            From order add-on / payment (non-editable)
          </Text>
        )}
      </Col>
      {paymentMethod === 'Bank Transfer' && (
        <Col xs={24} md={8}>
          <Form.Item
            name={[sectionKey, 'bankAccount']}
            label={<span style={compactLabelStyle}>Bank Account</span>}
            rules={[{ required: true, message: 'Select bank account' }]}
            style={{ ...compactFormItemStyle, padding: 0 }}
          >
            <Select
              allowClear
              placeholder="Select bank account"
              dropdownClassName="select-checkbox-dropdown"
              options={toSelectOptions(methodConfig?.accounts || [])}
              getPopupContainer={selectPopupContainer}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

/** Documitra Fee receipt body — category checkboxes + sections (matches CMS mock). */
const DocumitraFeeReceiptBody = ({
  form,
  formDetails,
  allowedMethods,
}: {
  form: any;
  formDetails?: any;
  allowedMethods?: string[];
}) => {
  const categories: string[] = Form.useWatch('documitraCategories', form) || [];
  const showAdditional = categories.includes('Add Ons');
  const showSales = categories.includes('Sales');
  const courierSelected = orderHasCourierAddon(formDetails);
  const apostilleSelected = orderHasApostilleLabelFee(formDetails);
  const hasAdditionalAddons = courierSelected || apostilleSelected;
  const addonPaymentMethod = resolveOrderPaymentMethod(formDetails);
  const lockedMethod =
    allowedMethods?.length && addonPaymentMethod && allowedMethods.includes(addonPaymentMethod)
      ? addonPaymentMethod
      : allowedMethods?.length
        ? allowedMethods[0]
        : addonPaymentMethod;

  const categoryOptions = [
    ...(hasAdditionalAddons ? [{ label: 'Add Ons', value: 'Add Ons' }] : []),
    { label: 'Sales', value: 'Sales' },
  ];

  return (
    <>
      <Form.Item
        name="documitraCategories"
        label={<span style={compactLabelStyle}>Documitra Fee Category</span>}
        rules={[
          {
            validator: async (_, value) => {
              if (!value || !value.length) {
                throw new Error('Select at least one Documitra Fee category');
              }
            },
          },
        ]}
        style={{
          ...compactFormItemStyle,
          padding: 0,
          marginBottom: 14,
          borderBottom: 'none',
        }}
      >
        <Checkbox.Group
          options={categoryOptions}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}
        />
      </Form.Item>

      {showAdditional && hasAdditionalAddons && (
        <Row gutter={[12, 12]}>
          {courierSelected && (
            <DocumitraFeeSectionFields
              sectionKey="courierFee"
              title="Courier Label Fee"
              form={form}
              lockedMethod={lockedMethod}
              methodEditable={false}
              amountEditable={false}
              amountNote={orderCourierLabelFee(formDetails).note}
              hidePaymentFields
              allowedMethods={allowedMethods}
            />
          )}
          {apostilleSelected && (
            <DocumitraFeeSectionFields
              sectionKey="apostilleFee"
              title="Apostille Fee"
              form={form}
              lockedMethod={lockedMethod}
              methodEditable={false}
              amountEditable={false}
              amountNote={orderApostilleFee(formDetails).note}
              hidePaymentFields
              allowedMethods={allowedMethods}
            />
          )}
        </Row>
      )}

      {showAdditional && hasAdditionalAddons && showSales && (
        <Divider orientation="left" orientationMargin={0} style={{ marginBottom: 0 }}>
          <span style={{ ...compactLabelStyle, color: '#004195' }}>Sales Fees</span>
        </Divider>
      )}

      {showSales && (
        <>
          <DocumitraFeeSectionFields
            sectionKey="sales"
            title="Received Amount"
            form={form}
            methodEditable={true}
            amountEditable={true}
            allowedMethods={allowedMethods}
          />
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="senderName"
                label={<span style={compactLabelStyle}>Sender Name</span>}
                rules={[{ required: true, message: 'Enter sender name' }]}
                style={{ ...compactFormItemStyle, padding: 0 }}
              >
                <Input placeholder="Name of sender" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="transactionId"
                label={<span style={compactLabelStyle}>Transaction / Reference ID</span>}
                rules={[{ required: true, message: 'Enter transaction / reference ID' }]}
                style={{ ...compactFormItemStyle, padding: 0 }}
              >
                <Input placeholder="TXN-00000" />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

const buildDocumitraReceiptLines = (
  values: any,
  formDetails?: any
): Array<{
  feeCategory: string;
  paymentMethod: string;
  bankAccount?: string;
  receivedAmount: number;
  receivedDate: string;
  receiptSource: string;
  senderName?: string;
  transactionId?: string;
}> => {
  const categories: string[] = values.documitraCategories || [];
  const showAdditional = categories.includes('Add Ons');
  const showSales = categories.includes('Sales');
  const sections: Array<{ key: string; feeCategory: string; include: boolean }> = [
    {
      key: 'courierFee',
      feeCategory: 'Courier Label',
      include: showAdditional && orderHasCourierAddon(formDetails),
    },
    {
      key: 'apostilleFee',
      feeCategory: 'Apostille Fee',
      include: showAdditional && orderHasApostilleLabelFee(formDetails),
    },
    {
      key: 'sales',
      feeCategory: 'Sales',
      include: showSales,
    },
  ];

  const senderName = toOptionalString(values.senderName);
  const transactionId = toOptionalString(values.transactionId);

  return sections
    .filter(section => section.include)
    .map(section => {
      const data = values[section.key];
      // Add-on amounts are fixed ($40 per member / apostille label), never taken from the form.
      const amount =
        section.feeCategory === 'Courier Label'
          ? orderCourierLabelFee(formDetails).amount
          : section.feeCategory === 'Apostille Fee'
            ? orderApostilleFee(formDetails).amount
            : Number(data?.receivedAmount || 0);
      const method = data?.paymentMethod;
      const date = data?.paymentDate;
      if (!(amount > 0) || !method || !date) return null;
      return {
        feeCategory: section.feeCategory,
        paymentMethod: method,
        bankAccount: data?.bankAccount,
        receivedAmount: amount,
        receivedDate: date.format('YYYY-MM-DD'),
        receiptSource: 'From Documitra Fee',
        senderName: section.feeCategory === 'Sales' ? senderName : undefined,
        transactionId: section.feeCategory === 'Sales' ? transactionId : undefined,
      };
    })
    .filter(Boolean) as Array<{
    feeCategory: string;
    paymentMethod: string;
    bankAccount?: string;
    receivedAmount: number;
    receivedDate: string;
    receiptSource: string;
    senderName?: string;
    transactionId?: string;
  }>;
};

const CreateVoucherForm = ({
  orderId,
  formDetails,
  onCancel,
  onCreated,
}: {
  orderId: any;
  formDetails?: any;
  onCancel: () => void;
  onCreated: (voucher: any) => void;
}) => {
  const addonPaymentMethod = resolveOrderPaymentMethod(formDetails);
  const courierSelected = orderHasCourierAddon(formDetails);
  const apostilleSelected = orderHasApostilleLabelFee(formDetails);
  const hasAdditionalAddons = orderHasAdditionalFeeAddons(formDetails);

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const receiptSource = Form.useWatch('receiptSource', form) || 'From Government Fee';
  const isDocumitraFee = receiptSource === 'From Documitra Fee';
  const documitraCategories: string[] = Form.useWatch('documitraCategories', form) || [];
  const showReceiptImage = !isDocumitraFee || documitraCategories.includes('Sales');
  const paymentMethod = Form.useWatch('paymentMethod', form);
  const imageFiles = Form.useWatch('image', form) || EMPTY_FILE_LIST;
  const imagePreviewUrl = useUploadImagePreview(imageFiles);
  const methodConfig = paymentMethod ? (VOUCHER_PAYMENT_METHODS as any)[paymentMethod] : null;
  const currency = methodConfig?.currency || 'USD';

  useEffect(() => {
    if (!isDocumitraFee) return;
    const defaultCategories = [...(hasAdditionalAddons ? ['Add Ons'] : []), 'Sales'];
    form.setFieldsValue({
      documitraCategories: defaultCategories,
      courierFee: courierSelected
        ? {
            receivedAmount: orderCourierLabelFee(formDetails).amount,
            paymentDate: moment(),
            paymentMethod: addonPaymentMethod,
          }
        : undefined,
      apostilleFee: apostilleSelected
        ? {
            receivedAmount: orderApostilleFee(formDetails).amount,
            paymentDate: moment(),
            paymentMethod: addonPaymentMethod,
          }
        : undefined,
      sales: {
        receivedAmount: undefined,
        paymentDate: moment(),
        paymentMethod: undefined,
      },
    });
  }, [
    isDocumitraFee,
    courierSelected,
    apostilleSelected,
    hasAdditionalAddons,
    addonPaymentMethod,
    form,
  ]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (values.receiptSource === 'From Documitra Fee') {
        const lines = buildDocumitraReceiptLines(form.getFieldsValue(true), formDetails);
        if (!lines.length) {
          toastError(
            'Fill Received Amount, Payment Date and Payment Method for at least one Documitra Fee section'
          );
          setSaving(false);
          return;
        }
        const currencies = Array.from(
          new Set(
            lines.map(
              line => (VOUCHER_PAYMENT_METHODS as any)[line.paymentMethod]?.currency || 'USD'
            )
          )
        );
        if (currencies.length > 1) {
          toastError('All Documitra Fee sections must use the same currency (USD or INR)');
          setSaving(false);
          return;
        }

        const [first, ...rest] = lines;
        const createData = new FormData();
        createData.append('orderId', String(orderId));
        appendReceiptFields(createData, first);
        const imageFile = getUploadFile(values.image);
        if (imageFile && first.feeCategory === 'Sales') {
          createData.append('image', imageFile);
        }

        const created: any = await uploadImageApiCall(PAYMENT_VOUCHER, createData);
        if (created?.isSuccess !== 1) {
          toastError(created?.message || 'Failed to record receipt');
          setSaving(false);
          return;
        }

        let latestVoucher = created?.data;
        const voucherKey = getVoucherKey(latestVoucher);
        for (const line of rest) {
          if (!voucherKey) break;
          const lineData = new FormData();
          appendReceiptFields(lineData, line);
          if (imageFile && line.feeCategory === 'Sales') {
            lineData.append('image', imageFile);
          }
          const added: any = await uploadImageApiCall(
            `${PAYMENT_VOUCHER}/${voucherKey}/receipts`,
            lineData,
            'POST'
          );
          if (added?.isSuccess === 1 && added?.data?.voucher) {
            latestVoucher = added.data.voucher;
          }
        }

        toastSuccess('Documitra Fee receipt recorded successfully');
        onCreated(latestVoucher);
        return;
      }

      const formData = new FormData();
      formData.append('orderId', String(orderId));
      appendReceiptFields(formData, {
        paymentMethod: values.paymentMethod,
        bankAccount: values.bankAccount,
        receivedAmount: values.receivedAmount,
        receivedDate: values.receivedDate.format('YYYY-MM-DD'),
        receiptSource: 'From Government Fee',
        senderName: values.senderName,
        transactionId: values.transactionId,
      });
      const imageFile = getUploadFile(values.image);
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const res: any = await uploadImageApiCall(PAYMENT_VOUCHER, formData);
      if (res?.isSuccess === 1) {
        toastSuccess('Receipt recorded successfully');
        onCreated(res?.data);
      } else {
        toastError(res?.message || 'Failed to record receipt');
      }
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError('Failed to record receipt. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginBottom: 16,
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        padding: 16,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          receiptSource: 'From Government Fee',
          receivedDate: moment(),
        }}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24}>
            <Form.Item
              name="receiptSource"
              label={<span style={compactLabelStyle}>Record Receipt From</span>}
              rules={[{ required: true, message: 'Select receipt source' }]}
              style={{ ...compactFormItemStyle, padding: 0 }}
            >
              <Radio.Group
                options={RECEIPT_SOURCES.map(source => ({ label: source, value: source }))}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              />
            </Form.Item>
          </Col>
        </Row>

        {isDocumitraFee ? (
          <DocumitraFeeReceiptBody form={form} formDetails={formDetails} />
        ) : (
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="paymentMethod"
                label={<span style={compactLabelStyle}>Payment Method</span>}
                rules={[{ required: true, message: 'Select payment method' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Radio.Group
                  options={Object.keys(VOUCHER_PAYMENT_METHODS).map(method => ({
                    label: method,
                    value: method,
                  }))}
                  onChange={() => form.setFieldsValue({ bankAccount: undefined })}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
                />
              </Form.Item>
            </Col>
            {paymentMethod === 'Bank Transfer' && (
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  name="bankAccount"
                  label={<span style={compactLabelStyle}>Bank Account</span>}
                  rules={[{ required: true, message: 'Select bank account' }]}
                  style={{ ...compactFormItemStyle, padding: '0' }}
                >
                  <Select
                    allowClear
                    placeholder="Select bank account"
                    dropdownClassName="select-checkbox-dropdown"
                    options={toSelectOptions(methodConfig?.accounts || [])}
                    getPopupContainer={selectPopupContainer}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="receivedAmount"
                label={<span style={compactLabelStyle}>Received Amount</span>}
                rules={[{ required: true, message: 'Enter received amount' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <InputNumber<number>
                  key={currency}
                  style={{ width: '100%', alignContent: 'center' }}
                  min={0.01}
                  precision={2}
                  controls={false}
                  formatter={value => `${currencySymbol(currency)} ${value}`}
                  parser={value => Number(value?.replace(/[^\d.]/g, ''))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="receivedDate"
                label={<span style={compactLabelStyle}>Payment Date</span>}
                rules={[{ required: true, message: 'Select payment date' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="senderName"
                label={<span style={compactLabelStyle}>Sender Name</span>}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Input placeholder="Name of sender" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="transactionId"
                label={<span style={compactLabelStyle}>Transaction / Reference ID</span>}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Input placeholder="TXN-00000" />
              </Form.Item>
            </Col>
          </Row>
        )}

        {showReceiptImage && (
          <Row gutter={[12, 12]} style={{ marginTop: 0, marginBottom: 20 }}>
            <Col xs={24}>
              <Form.Item
                name="image"
                label={<span style={compactLabelStyle}>Receipt Image</span>}
                valuePropName="fileList"
                getValueFromEvent={normFile}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Dragger
                  beforeUpload={() => false}
                  maxCount={1}
                  accept="image/*"
                  showUploadList={false}
                  style={{ padding: '8px', minHeight: 96 }}
                >
                  {imagePreviewUrl ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={imagePreviewUrl}
                        alt="Receipt preview"
                        style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          display: 'flex',
                          gap: 4,
                        }}
                      >
                        <Button
                          type="primary"
                          shape="circle"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={event => {
                            event.stopPropagation();
                            window.open(imagePreviewUrl, '_blank', 'noopener,noreferrer');
                          }}
                        />
                        <Button
                          danger
                          shape="circle"
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={event => {
                            event.stopPropagation();
                            form.setFieldsValue({ image: [] });
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text" style={{ marginBottom: 4, fontSize: 13 }}>
                        Upload image
                      </p>
                    </>
                  )}
                </Dragger>
              </Form.Item>
            </Col>
          </Row>
        )}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: isDocumitraFee && documitraCategories.includes('Add Ons') ? 20 : 0,
          }}
        >
          <InvertFillButton
            variant="blue"
            size="sm"
            fontWeight="semibold"
            onClick={handleSave}
            disabled={saving}
          >
            Record Receipt
          </InvertFillButton>
          <InvertFillButton variant="red" size="sm" fontWeight="semibold" onClick={onCancel}>
            Cancel
          </InvertFillButton>
        </div>
      </Form>
    </div>
  );
};

const AddReceiptForm = ({
  voucher,
  receipt,
  formDetails,
  onCancel,
  onSaved,
}: {
  voucher: any;
  receipt?: any;
  formDetails?: any;
  onCancel: () => void;
  onSaved: (voucher: any) => void;
}) => {
  const isEdit = Boolean(receipt?.id);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const hasDocumitraReceipt = voucherHasDocumitraFeeReceipt(voucher);
  const receiptSourceOptions = RECEIPT_SOURCES.filter(
    source => !(isDocumitraReceiptSource(source) && hasDocumitraReceipt && !isEdit)
  );
  const receiptSource = Form.useWatch('receiptSource', form) || 'From Government Fee';
  const isDocumitraFee = isDocumitraReceiptSource(receiptSource) && !isEdit;
  const documitraCategories: string[] = Form.useWatch('documitraCategories', form) || [];
  const showReceiptImage = !isDocumitraFee || documitraCategories.includes('Sales');
  const paymentMethod = Form.useWatch('paymentMethod', form);
  const imageFiles = Form.useWatch('image', form) || EMPTY_FILE_LIST;
  const imagePreviewUrl = useUploadImagePreview(imageFiles);
  const methodConfig = paymentMethod ? (VOUCHER_PAYMENT_METHODS as any)[paymentMethod] : null;
  const currency = methodConfig?.currency || voucher.currency || 'USD';
  const allowedMethods = Object.keys(VOUCHER_PAYMENT_METHODS).filter(
    method =>
      !voucher.currency || (VOUCHER_PAYMENT_METHODS as any)[method]?.currency === voucher.currency
  );
  const methodOptions = allowedMethods.length
    ? allowedMethods
    : Object.keys(VOUCHER_PAYMENT_METHODS);
  const addonPaymentMethod = resolveOrderPaymentMethod(formDetails);
  const lockedAddonMethod =
    addonPaymentMethod && methodOptions.includes(addonPaymentMethod)
      ? addonPaymentMethod
      : methodOptions[0];
  const courierSelected = orderHasCourierAddon(formDetails);
  const apostilleSelected = orderHasApostilleLabelFee(formDetails);
  const hasAdditionalAddons = orderHasAdditionalFeeAddons(formDetails);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        receiptSource: 'From Government Fee',
      });
      return;
    }
    form.setFieldsValue({
      receiptSource: receipt.receiptSource || 'From Government Fee',
      paymentMethod: receipt.paymentMethod,
      bankAccount: receipt.bankAccount || undefined,
      receivedAmount: Number(receipt.amount),
      receivedDate: receipt.receivedDate ? moment(receipt.receivedDate) : moment(),
      senderName: receipt.senderName || undefined,
      transactionId: receipt.transactionId || undefined,
      image: receipt.imageUrl
        ? [
            {
              uid: 'existing-receipt',
              name: 'Receipt image',
              status: 'done',
              url: receipt.imageUrl,
            },
          ]
        : [],
    });
  }, [isEdit, receipt, form]);

  useEffect(() => {
    if (!isDocumitraFee) return;
    const defaultCategories = [...(hasAdditionalAddons ? ['Add Ons'] : []), 'Sales'];
    form.setFieldsValue({
      documitraCategories: defaultCategories,
      courierFee: courierSelected
        ? {
            receivedAmount: orderCourierLabelFee(formDetails).amount,
            paymentDate: moment(),
            paymentMethod: lockedAddonMethod,
          }
        : undefined,
      apostilleFee: apostilleSelected
        ? {
            receivedAmount: orderApostilleFee(formDetails).amount,
            paymentDate: moment(),
            paymentMethod: lockedAddonMethod,
          }
        : undefined,
      sales: {
        receivedAmount: undefined,
        paymentDate: moment(),
        paymentMethod: undefined,
      },
    });
  }, [
    isDocumitraFee,
    courierSelected,
    apostilleSelected,
    hasAdditionalAddons,
    lockedAddonMethod,
    form,
  ]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const voucherKey = getVoucherKey(voucher);
      if (!voucherKey) {
        toastError('Payment voucher is missing. Please record a receipt first.');
        return;
      }
      setSaving(true);

      if (!isEdit && values.receiptSource === 'From Documitra Fee') {
        if (hasDocumitraReceipt) {
          toastError('Documitra Fee receipt has already been recorded for this voucher');
          setSaving(false);
          return;
        }
        const lines = buildDocumitraReceiptLines(form.getFieldsValue(true), formDetails);
        if (!lines.length) {
          toastError(
            'Fill Received Amount, Payment Date and Payment Method for at least one Documitra Fee section'
          );
          setSaving(false);
          return;
        }
        const invalidCurrency = lines.some(
          line =>
            voucher.currency &&
            (VOUCHER_PAYMENT_METHODS as any)[line.paymentMethod]?.currency !== voucher.currency
        );
        if (invalidCurrency) {
          toastError(
            `This voucher is in ${voucher.currency}. Documitra Fee must use a ${voucher.currency} payment method.`
          );
          setSaving(false);
          return;
        }

        let latestVoucher = voucher;
        for (const line of lines) {
          const lineData = new FormData();
          appendReceiptFields(lineData, line);
          const imageFile = getUploadFile(values.image);
          if (imageFile && line.feeCategory === 'Sales') {
            lineData.append('image', imageFile);
          }
          const added: any = await uploadImageApiCall(
            `${PAYMENT_VOUCHER}/${voucherKey}/receipts`,
            lineData,
            'POST'
          );
          if (added?.isSuccess !== 1) {
            toastError(added?.message || 'Failed to record Documitra Fee receipt');
            setSaving(false);
            return;
          }
          if (added?.data?.voucher) {
            latestVoucher = added.data.voucher;
          }
        }

        toastSuccess('Documitra Fee receipt recorded successfully');
        onSaved(latestVoucher);
        return;
      }

      const formData = new FormData();
      formData.append('paymentMethod', values.paymentMethod);
      if (values.paymentMethod === 'Bank Transfer' && values.bankAccount) {
        formData.append('bankAccount', values.bankAccount);
      }
      formData.append('receivedAmount', String(values.receivedAmount));
      formData.append('receivedDate', values.receivedDate.format('YYYY-MM-DD'));
      formData.append('receiptSource', values.receiptSource || 'From Government Fee');
      const transactionId = toOptionalString(values.transactionId);
      if (transactionId) {
        formData.append('transactionId', transactionId);
      }
      const senderName = toOptionalString(values.senderName);
      if (senderName) {
        formData.append('senderName', senderName);
      }
      const imageFile = getUploadFile(values.image);
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const endpoint = isEdit
        ? `${PAYMENT_VOUCHER}/${voucherKey}/receipts/${receipt.id}`
        : `${PAYMENT_VOUCHER}/${voucherKey}/receipts`;
      const res: any = await uploadImageApiCall(endpoint, formData, isEdit ? 'PUT' : 'POST');
      if (res?.isSuccess === 1) {
        toastSuccess(isEdit ? 'Receipt updated successfully' : 'Receipt recorded successfully');
        onSaved(res?.data?.voucher);
      } else {
        toastError(
          res?.message || (isEdit ? 'Failed to update receipt' : 'Failed to record receipt')
        );
      }
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(
          isEdit
            ? 'Failed to update receipt. Please try again.'
            : 'Failed to record receipt. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginBottom: 16,
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        padding: 16,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          receiptSource: 'From Government Fee',
          receivedDate: moment(),
        }}
      >
        {!isEdit && (
          <Row gutter={[12, 12]}>
            <Col xs={24}>
              <Form.Item
                name="receiptSource"
                label={<span style={compactLabelStyle}>Record Receipt From</span>}
                rules={[{ required: true, message: 'Select receipt source' }]}
                style={{ ...compactFormItemStyle, padding: 0 }}
              >
                <Radio.Group
                  options={receiptSourceOptions.map(source => ({
                    label: source,
                    value: source,
                  }))}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
                />
              </Form.Item>
              {hasDocumitraReceipt && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
                  Documitra Fee receipt is already recorded for this voucher (allowed once). You can
                  still record Government Fee receipts unlimited times.
                </Text>
              )}
            </Col>
          </Row>
        )}

        {isDocumitraFee ? (
          <DocumitraFeeReceiptBody
            form={form}
            formDetails={formDetails}
            allowedMethods={methodOptions}
          />
        ) : (
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="paymentMethod"
                label={
                  <span style={compactLabelStyle}>
                    Payment Method [ {currencySymbol(currency)} ]
                  </span>
                }
                rules={[{ required: true, message: 'Select payment method' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Radio.Group
                  options={methodOptions.map(method => ({
                    label: method,
                    value: method,
                  }))}
                  onChange={() => form.setFieldsValue({ bankAccount: undefined })}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
                />
              </Form.Item>
            </Col>
            {paymentMethod === 'Bank Transfer' && (
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  name="bankAccount"
                  label={<span style={compactLabelStyle}>Bank Account</span>}
                  rules={[{ required: true, message: 'Select bank account' }]}
                  style={{ ...compactFormItemStyle, padding: '0' }}
                >
                  <Select
                    allowClear
                    placeholder="Select bank account"
                    dropdownClassName="select-checkbox-dropdown"
                    options={toSelectOptions(methodConfig?.accounts || [])}
                    getPopupContainer={selectPopupContainer}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="receivedAmount"
                label={<span style={compactLabelStyle}>Received Amount</span>}
                rules={[{ required: true, message: 'Enter received amount' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <InputNumber<number>
                  key={currency}
                  style={{ width: '100%', textAlign: 'center', alignContent: 'center' }}
                  min={0.01}
                  precision={2}
                  controls={false}
                  formatter={value => `${currencySymbol(currency)} ${value}`}
                  parser={value => Number(value?.replace(/[^\d.]/g, ''))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="receivedDate"
                label={<span style={compactLabelStyle}>Received Date</span>}
                rules={[{ required: true, message: 'Select received date' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="senderName"
                label={<span style={compactLabelStyle}>Sender Name</span>}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Input placeholder="Name of sender" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="transactionId"
                label={<span style={compactLabelStyle}>Transaction / Reference ID</span>}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Input placeholder="TXN-00000" />
              </Form.Item>
            </Col>
          </Row>
        )}

        {showReceiptImage && (
          <Row gutter={[12, 12]} style={{ marginTop: 0, marginBottom: 20 }}>
            <Col xs={24}>
              <Form.Item
                name="image"
                label={
                  <span style={compactLabelStyle}>
                    {isEdit && receipt?.imageUrl
                      ? 'Receipt Image (upload to replace)'
                      : 'Receipt Image'}
                  </span>
                }
                valuePropName="fileList"
                getValueFromEvent={normFile}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Dragger
                  beforeUpload={() => false}
                  maxCount={1}
                  accept="image/*"
                  showUploadList={false}
                  style={{ padding: '16px', minHeight: 120 }}
                >
                  {imagePreviewUrl ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={imagePreviewUrl}
                        alt="Receipt preview"
                        style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          display: 'flex',
                          gap: 4,
                        }}
                      >
                        <Button
                          type="primary"
                          shape="circle"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={event => {
                            event.stopPropagation();
                            window.open(imagePreviewUrl, '_blank', 'noopener,noreferrer');
                          }}
                        />
                        <Button
                          danger
                          shape="circle"
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={event => {
                            event.stopPropagation();
                            form.setFieldsValue({ image: [] });
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text" style={{ marginBottom: 4, fontSize: 13 }}>
                        Upload image
                      </p>
                    </>
                  )}
                </Dragger>
              </Form.Item>
            </Col>
          </Row>
        )}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: isDocumitraFee && documitraCategories.includes('Add Ons') ? 20 : 0,
          }}
        >
          <InvertFillButton
            variant="blue"
            size="sm"
            fontWeight="semibold"
            onClick={handleSave}
            disabled={saving}
          >
            {isEdit ? 'Update Receipt' : 'Record Receipt'}
          </InvertFillButton>
          <InvertFillButton variant="red" size="sm" fontWeight="semibold" onClick={onCancel}>
            Cancel
          </InvertFillButton>
        </div>
      </Form>
    </div>
  );
};

const ReceiptsSection = ({
  voucher,
  formDetails,
  onSaved,
  allowRecordReceipt = true,
}: {
  voucher: any;
  formDetails?: any;
  onSaved: (voucher: any) => void;
  /** When false (opened from Payment Vouchers list), Record Receipt is disabled. */
  allowRecordReceipt?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState<any>(null);
  const isSuperAdmin = getRole() === Role.SUPER_ADMIN;
  const canAddReceipt = allowRecordReceipt && !voucher.cancelled && voucher.status !== 'Settled';
  const canEditReceipts =
    allowRecordReceipt && isSuperAdmin && !voucher.cancelled && voucher.status !== 'Settled';
  const receipts = [...(voucher.receipts || [])].sort(
    (a: any, b: any) => Number(a.id) - Number(b.id)
  );

  const closeForms = () => {
    setOpen(false);
    setEditingReceipt(null);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <Text strong>Customer Receipts</Text>
        {canAddReceipt && (
          <InvertFillButton
            variant={open && !editingReceipt ? 'red' : 'white'}
            size="sm"
            fontWeight="semibold"
            onClick={() => {
              setEditingReceipt(null);
              setOpen(!open);
            }}
          >
            {open && !editingReceipt ? 'Cancel' : '+ Record Receipt'}
          </InvertFillButton>
        )}
      </div>
      {/* {!allowRecordReceipt && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Payment received cannot be recorded from this view.
        </Text>
      )} */}
      {allowRecordReceipt && voucher.status === 'Settled' && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          This voucher is Settled. Receipts are read-only.
        </Text>
      )}
      {(open || editingReceipt) && canAddReceipt && (
        <AddReceiptForm
          voucher={voucher}
          formDetails={formDetails}
          receipt={editingReceipt || undefined}
          onCancel={closeForms}
          onSaved={updated => {
            closeForms();
            onSaved(updated);
          }}
        />
      )}
      {receipts.length ? (
        <Table
          rowKey="id"
          size="small"
          bordered
          pagination={false}
          dataSource={receipts}
          columns={[
            // {
            //   title: '#',
            //   width: 50,
            //   render: (_: any, __: any, index: number) => index + 1,
            // },
            {
              title: 'Source / Fee',
              key: 'sourceFee',
              render: (_: any, row: any) =>
                [row.receiptSource, row.feeCategory].filter(Boolean).join(' · ') || '—',
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              render: (value: any) => money(value, voucher.currency),
            },
            {
              title: 'Received Date',
              dataIndex: 'receivedDate',
              render: (value: any) => displayDate(value),
            },
            {
              title: 'Method',
              dataIndex: 'paymentMethod',
              render: (value: any, row: any) =>
                row.bankAccount ? `${value} — ${row.bankAccount}` : value || '—',
            },
            {
              title: 'Sender Name',
              dataIndex: 'senderName',
              render: (value: any) => displayText(value),
            },
            {
              title: 'Created On',
              dataIndex: 'createdAt',
              render: (value: any) => displayDate(value),
            },
            {
              title: 'Recorded By',
              dataIndex: 'addedBy',
              render: (value: any) => displayText(value),
            },
            {
              title: 'Transaction ID',
              dataIndex: 'transactionId',
              render: (value: any) => displayText(value),
            },
            {
              title: 'Image',
              dataIndex: 'imageUrl',
              render: (value: any) =>
                value ? (
                  <a href={value} target="_blank" rel="noopener noreferrer">
                    View
                  </a>
                ) : (
                  '—'
                ),
            },
            ...(canEditReceipts
              ? [
                  {
                    title: 'Action',
                    key: 'action',
                    width: 80,
                    render: (_: any, row: any) => (
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => {
                          setOpen(false);
                          setEditingReceipt(row);
                        }}
                      >
                        Edit
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      ) : (
        <Text type="secondary">No customer receipts yet.</Text>
      )}
    </div>
  );
};

const AddWithdrawalForm = ({
  voucher,
  onCancel,
  onSaved,
}: {
  voucher: any;
  onCancel: () => void;
  onSaved: (voucher: any) => void;
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const feeType = Form.useWatch('feeType', form);
  const paymentMode = Form.useWatch('paymentMode', form);

  const isCourier = isCourierFeeType(feeType);
  const isGovernment = feeType === 'Government Fee' || feeType === 'Other Charges';
  const isRefund = isRefundFeeType(feeType);
  const accountOptions =
    isGovernment && paymentMode ? (WITHDRAW_PAYMENT_MODES as any)[paymentMode] : null;
  const paidBy = getAdminName() || 'CMS User';
  const members = normalizeOrderMembers(voucher?.members || []);

  const handleFeeTypeChange = () => {
    form.setFieldsValue({
      paymentMode: undefined,
      paymentAccount: undefined,
      paymentDate: undefined,
      govtReference: undefined,
      trackingId: undefined,
      portalName: undefined,
      purchasedDate: undefined,
      refundId: undefined,
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const voucherKey = getVoucherKey(voucher);
      if (!voucherKey) {
        toastError('Payment voucher is missing. Please record a receipt first.');
        return;
      }

      const selectedFeeType = values.feeType;
      if (!selectedFeeType) {
        toastError('Please select a fee type');
        return;
      }

      const modeAccounts = values.paymentMode
        ? (WITHDRAW_PAYMENT_MODES as any)[values.paymentMode]
        : null;
      const courier = isCourierFeeType(selectedFeeType);
      const government =
        selectedFeeType === 'Government Fee' || selectedFeeType === 'Other Charges';
      const refund = isRefundFeeType(selectedFeeType);

      setSaving(true);
      const res = await callApi(`${PAYMENT_VOUCHER}/${voucherKey}/withdrawals`, 'POST', {
        memberId: values.memberId,
        feeType: selectedFeeType,
        paymentMode: government ? values.paymentMode : undefined,
        paymentAccount: government && modeAccounts ? values.paymentAccount : undefined,
        amount: values.amount,
        // Refund Date shares the paymentDate column.
        paymentDate: government || refund ? values.paymentDate?.format('YYYY-MM-DD') : undefined,
        govtReference: government ? values.govtReference?.trim() : undefined,
        remark: values.remark?.trim() || undefined,
        trackingId: courier ? values.trackingId?.trim() : undefined,
        portalName: courier ? values.portalName?.trim() : undefined,
        purchasedDate:
          courier && values.purchasedDate ? values.purchasedDate.format('YYYY-MM-DD') : undefined,
        refundId: refund ? values.refundId?.trim() : undefined,
      });
      if (res?.status) {
        toastSuccess('Payment recorded successfully');
        onSaved(res?.data?.data?.voucher);
      } else {
        toastError(res?.data?.message || 'Failed to record payment');
      }
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError('Failed to record payment. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginBottom: 16,
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        padding: 16,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Form form={form} layout="vertical" initialValues={{ paymentDate: moment() }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              name="memberId"
              label={<span style={compactLabelStyle}>Member</span>}
              rules={[{ required: true, message: 'Select member' }]}
              style={{ ...compactFormItemStyle, padding: '0' }}
            >
              <Select
                allowClear
                placeholder={members.length ? 'Select member' : 'No members found for this order'}
                dropdownClassName="select-checkbox-dropdown"
                options={members.map((member: any) => ({
                  label: member.name,
                  value: String(member.id),
                }))}
                getPopupContainer={selectPopupContainer}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={16}>
            <Form.Item
              name="feeType"
              label={<span style={compactLabelStyle}>Fee Type $</span>}
              rules={[{ required: true, message: 'Select fee type' }]}
              style={{ ...compactFormItemStyle, padding: '0' }}
            >
              <Radio.Group
                options={toSelectOptions([...RECORD_PAYMENT_FEE_TYPES])}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
                onChange={handleFeeTypeChange}
              />
            </Form.Item>
          </Col>

          {isCourier && (
            <Col xs={24} md={8}>
              <Form.Item
                name="portalName"
                label={<span style={compactLabelStyle}>Portal Name</span>}
                rules={[{ required: true, message: 'Select portal name' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Radio.Group
                  options={toSelectOptions(COURIER_PORTAL_OPTIONS)}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
                />
              </Form.Item>
            </Col>
          )}

          {isGovernment && (
            <Col xs={24} md={8}>
              <Form.Item
                name="paymentMode"
                label={<span style={compactLabelStyle}>Payment Mode</span>}
                rules={[{ required: true, message: 'Select payment mode' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Select
                  allowClear
                  placeholder="Select payment mode"
                  dropdownClassName="select-checkbox-dropdown"
                  options={toSelectOptions(Object.keys(WITHDRAW_PAYMENT_MODES))}
                  getPopupContainer={selectPopupContainer}
                  onChange={() => form.setFieldsValue({ paymentAccount: undefined })}
                />
              </Form.Item>
            </Col>
          )}

          {isGovernment && accountOptions && (
            <Col xs={24} md={8}>
              <Form.Item
                name="paymentAccount"
                label={<span style={compactLabelStyle}>Account / Card</span>}
                rules={[{ required: true, message: 'Select account / card' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Select
                  allowClear
                  placeholder="Select account / card"
                  dropdownClassName="select-checkbox-dropdown"
                  options={toSelectOptions(accountOptions)}
                  getPopupContainer={selectPopupContainer}
                />
              </Form.Item>
            </Col>
          )}

          {isRefund && (
            <Col xs={24} md={8}>
              <Form.Item
                name="refundId"
                label={<span style={compactLabelStyle}>Refund ID</span>}
                rules={[{ required: true, message: 'Enter refund ID' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Input placeholder="e.g. RF-1024" />
              </Form.Item>
            </Col>
          )}

          <Col xs={24} md={8}>
            <Form.Item
              name="amount"
              label={
                <span style={compactLabelStyle}>{isRefund ? 'Refund Amount' : 'Amount Paid'}</span>
              }
              rules={[
                {
                  required: true,
                  message: isRefund ? 'Enter refund amount' : 'Enter amount paid',
                },
              ]}
              style={{ ...compactFormItemStyle, padding: '0' }}
            >
              <InputNumber
                style={{ width: '100%', alignContent: 'center' }}
                min={0.01}
                precision={2}
              />
            </Form.Item>
          </Col>

          {isRefund && (
            <Col xs={24} md={8}>
              <Form.Item
                name="paymentDate"
                label={<span style={compactLabelStyle}>Refund Date</span>}
                rules={[{ required: true, message: 'Select refund date' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            </Col>
          )}

          {isCourier && (
            <Col xs={24} md={8}>
              <Form.Item
                name="trackingId"
                label={<span style={compactLabelStyle}>Tracking ID</span>}
                rules={[{ required: true, message: 'Enter tracking ID' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Input placeholder="e.g. 1Z999AA1234" />
              </Form.Item>
            </Col>
          )}

          {isGovernment && (
            <Col xs={24} md={8}>
              <Form.Item
                name="paymentDate"
                label={<span style={compactLabelStyle}>Payment Date</span>}
                rules={[{ required: true, message: 'Select payment date' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            </Col>
          )}

          {isCourier && (
            <Col xs={24} md={8}>
              <Form.Item
                name="purchasedDate"
                label={<span style={compactLabelStyle}>Purchased Date</span>}
                rules={[{ required: true, message: 'Select purchased date' }]}
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            </Col>
          )}

          {isGovernment && (
            <Col xs={24} md={8}>
              <Form.Item
                name="govtReference"
                label={<span style={compactLabelStyle}>Govt. Reference</span>}
                rules={
                  feeType === 'Government Fee'
                    ? [{ required: true, message: 'Enter govt. reference' }]
                    : undefined
                }
                style={{ ...compactFormItemStyle, padding: '0' }}
              >
                <Input
                  placeholder={feeType === 'Government Fee' ? 'e.g. GOV-004' : 'Optional reference'}
                  style={{ height: '32px', overflow: 'hidden', alignItems: 'center' }}
                />
              </Form.Item>
            </Col>
          )}

          <Col xs={24} md={8}>
            <Form.Item
              label={<span style={compactLabelStyle}>Recorded By</span>}
              style={{ ...compactFormItemStyle, padding: '0' }}
            >
              <Input value={paidBy} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="remark"
              label={<span style={compactLabelStyle}>Remark</span>}
              style={{ ...compactFormItemStyle, padding: '0' }}
            >
              <Input
                placeholder={
                  isRefund ? 'Optional note about this refund' : 'Optional note about this payment'
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <InvertFillButton
            variant="blue"
            size="sm"
            fontWeight="semibold"
            onClick={handleSave}
            disabled={saving}
          >
            Record Payment
          </InvertFillButton>
          <InvertFillButton variant="red" size="sm" fontWeight="semibold" onClick={onCancel}>
            Cancel
          </InvertFillButton>
        </div>
      </Form>
    </div>
  );
};

const MemberWithdrawalTables = ({ voucher }: { voucher: any }) => {
  const members = voucher?.members || [];
  const withdrawals = voucher?.withdrawals || [];
  const membersWithWithdrawals = members.filter((member: any) =>
    withdrawals.some((row: any) => String(row.memberId) === String(member.id))
  );

  if (!withdrawals.length || !membersWithWithdrawals.length) {
    return (
      <div
        style={{
          padding: '16px',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px dashed #cbd5e1',
          marginBottom: 12,
        }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 0, fontSize: '13px' }}>
          No payments recorded yet. Use + Record Payment to add a member entry.
        </Text>
      </div>
    );
  }

  return (
    <>
      {membersWithWithdrawals.map((member: any) => {
        const rows = withdrawals.filter((row: any) => String(row.memberId) === String(member.id));
        const subtotal = rows.reduce((sum: number, row: any) => sum + Number(row.amount || 0), 0);
        return (
          <Card
            key={member.id}
            size="small"
            style={{
              marginBottom: 12,
              borderRadius: 12,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0',
            }}
            headStyle={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderBottom: '1px solid #e2e8f0',
              padding: '12px 16px',
            }}
            title={
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>
                  {member.name}
                </span>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  Withdrawn:{' '}
                  <Text strong style={{ color: '#004195' }}>
                    {money(subtotal, voucher.currency)}
                  </Text>
                </Text>
              </div>
            }
          >
            <Table
              rowKey="id"
              size="small"
              bordered
              pagination={false}
              columns={withdrawalColumns(voucher.currency)}
              dataSource={rows}
              scroll={{ x: true }}
            />
          </Card>
        );
      })}
    </>
  );
};

const SettlementSection = ({
  voucher,
  onSaved,
  defaultOpen = false,
  open,
  onToggle,
  showForm = true,
  showTable = true,
  onSetSaveAction,
  showActions = true,
}: {
  voucher: any;
  onSaved: (voucher: any) => void;
  defaultOpen?: boolean;
  open?: boolean;
  onToggle?: (next: boolean) => void;
  showForm?: boolean;
  showTable?: boolean;
  onSetSaveAction?: (save: () => Promise<void>) => void;
  showActions?: boolean;
}) => {
  const [form] = Form.useForm();
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [saving, setSaving] = useState(false);
  const remaining = Number(voucher?.remainingAmount || 0);
  const settlements = voucher?.settlements || [];
  const isOpen = open !== undefined ? open : internalOpen;

  const setOpenState = (next: boolean) => {
    if (onToggle) {
      onToggle(next);
      return;
    }
    setInternalOpen(next);
  };

  useEffect(() => {
    if (open === undefined) {
      setInternalOpen(defaultOpen);
    }
  }, [defaultOpen, open]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const voucherKey = getVoucherKey(voucher);
      if (!voucherKey) {
        toastError('Payment voucher is missing. Please create the voucher first.');
        return;
      }
      setSaving(true);
      const res = await callApi(`${PAYMENT_VOUCHER}/${voucherKey}/settlements`, 'POST', {
        amount: values.amount,
        settlementDate: values.settlementDate.format('YYYY-MM-DD'),
        note: values.note?.trim() || undefined,
      });
      if (res?.status) {
        toastSuccess('Settlement saved successfully');
        setOpenState(false);
        onSaved(res?.data?.data?.voucher);
      } else {
        toastError(res?.data?.message || 'Failed to save settlement');
      }
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError('Failed to save settlement. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    onSetSaveAction?.(handleSave);
  }, [onSetSaveAction, handleSave]);

  return (
    <div>
      {showTable && (
        <div
        // style={{
        //   borderTop: showForm && isOpen ? '1px solid #f0f0f0' : '1px solid #f0f0f0',
        //   paddingTop: showForm && isOpen ? 16 : 0,
        // }}
        >
          <Text strong style={{ display: 'block', margin: '0 0px' }}>
            Settled Amount
          </Text>
          {settlements.length ? (
            <Table
              rowKey="id"
              size="small"
              bordered
              pagination={false}
              style={{ marginBottom: 12 }}
              dataSource={settlements}
              columns={[
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  render: (value: any) => money(value, voucher.currency),
                },
                {
                  title: 'Date',
                  dataIndex: 'settlementDate',
                  render: (value: any) => displayDate(value),
                },
                { title: 'Note', dataIndex: 'note', render: (value: any) => value || '—' },
                {
                  title: 'Recorded By',
                  dataIndex: 'addedBy',
                  render: (value: any) => value || '—',
                },
              ]}
            />
          ) : (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {/* No amount has been settled yet. */}
            </Text>
          )}
        </div>
      )}
      {showTable && remaining > 0.004 && !voucher.cancelled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <InvertFillButton
            variant="red"
            size="sm"
            fontWeight="semibold"
            onClick={() => {
              if (isOpen) {
                void handleSave();
              } else {
                setOpenState(true);
              }
            }}
            disabled={saving}
          >
            {isOpen ? 'Save Settlement' : 'Settle Remaining Amount'}
          </InvertFillButton>
          {isOpen && (
            <InvertFillButton
              variant="white"
              size="sm"
              fontWeight="semibold"
              onClick={() => setOpenState(false)}
            >
              Cancel
            </InvertFillButton>
          )}
        </div>
      )}
      {showForm && remaining > 0.004 && !voucher.cancelled && isOpen && (
        <Form
          form={form}
          layout="vertical"
          // style={{ marginBottom: 16 }}
          initialValues={{
            amount: remaining,
            settlementDate: moment(),
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="amount"
                label="Settlement Amount"
                rules={[{ required: true, message: 'Enter amount' }]}
                style={{ padding: '0' }}
              >
                <InputNumber
                  style={{ width: '100%', alignContent: 'center' }}
                  min={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="settlementDate"
                label="Settlement Date"
                rules={[{ required: true, message: 'Select date' }]}
                style={{ padding: '0' }}
              >
                <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="note" label="Note" style={{ padding: '0' }}>
                <Input placeholder="e.g. Balance retained as buffer" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </div>
  );
};

const isClosedVoucherStatus = (voucher: any) =>
  Boolean(voucher) &&
  (Boolean(voucher.cancelled) || voucher.status === 'Settled' || voucher.status === 'Cancelled');

const getHistoryActionLabel = (action: any) => {
  const normalizedAction = String(action || '').toLowerCase();
  if (normalizedAction.includes('create') || normalizedAction.includes('generat')) {
    return 'Voucher Generated';
  }
  if (normalizedAction.includes('fee')) return 'Partial Payment Made';
  if (normalizedAction.includes('settle')) return 'Voucher Settled';

  const label = String(action || 'Voucher updated')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();

  return label ? label.replace(/\b\w/g, character => character.toUpperCase()) : 'Voucher updated';
};

const getHistoryDescription = (entry: any, currency?: string) => {
  const normalizedAction = String(entry?.action || '').toLowerCase();
  const formattedAmount = (value: any) => (value != null ? money(value, currency) : '—');

  if (normalizedAction.includes('create') || normalizedAction.includes('generat')) {
    return `Received Amount: ${formattedAmount(entry.receivedAmount ?? entry.amount)}`;
  }

  if (normalizedAction.includes('fee')) {
    return [
      entry.feeType || 'Fee',
      entry.memberName || '—',
      `Amount Paid: ${formattedAmount(entry.amountPaid ?? entry.amount)}`,
    ].join(' | ');
  }

  if (normalizedAction.includes('settle')) {
    return `Amount Settled: ${formattedAmount(entry.settledAmount ?? entry.amount)}`;
  }

  const details = [
    entry.memberName,
    entry.feeType,
    entry.amount != null && `Amount Paid: ${formattedAmount(entry.amount)}`,
  ].filter(Boolean);

  return details.length
    ? details.join(' | ')
    : `${getHistoryActionLabel(entry.action)} Please generate another voucher to continue.`;
};

const getHistoryIcon = (action: any) => {
  const normalizedAction = String(action || '').toLowerCase();
  if (normalizedAction.includes('cancel')) return <XCircle size={18} />;
  if (normalizedAction.includes('withdraw') || normalizedAction.includes('payment')) {
    return <CreditCard size={18} />;
  }
  if (normalizedAction.includes('settle')) return <CheckCircle2 size={18} />;
  if (normalizedAction.includes('create') || normalizedAction.includes('generat')) {
    return <FileText size={18} />;
  }
  return <Clock size={18} />;
};

const getHistoryIconColors = (action: any) => {
  const normalizedAction = String(action || '').toLowerCase();
  if (normalizedAction.includes('cancel')) return { background: '#fff1f0', color: '#d4380d' };
  if (normalizedAction.includes('withdraw')) return { background: '#fff7e6', color: '#d48806' };
  if (normalizedAction.includes('payment')) return { background: '#e6fffb', color: '#08979c' };
  if (normalizedAction.includes('settle')) return { background: '#f6ffed', color: '#389e0d' };
  if (normalizedAction.includes('create')) return { background: '#fff0f6', color: '#c41d7f' };
  return { background: '#fffbe6', color: '#d48806' };
};

const VoucherTrackingHistory = ({
  history = [],
  currency,
}: {
  history?: any[];
  currency?: string;
}) => {
  const entries = [...(Array.isArray(history) ? history : [])].sort(
    (first, second) =>
      new Date(first?.createdAt || 0).getTime() - new Date(second?.createdAt || 0).getTime()
  );

  return (
    <div
      style={{
        marginTop: '20px',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        padding: '24px 28px',
      }}
    >
      <div
        style={{
          marginTop: 0,
          marginBottom: '22px',
          fontSize: '15px',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          color: '#004195',
          paddingBottom: '8px',
          borderBottom: '1px solid rgba(0,48,135,0.15)',
        }}
      >
        Tracking History
      </div>

      {entries.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: '14px' }}>No tracking history yet.</div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: '42px' }}>
          {entries.map((entry, index) => {
            const isLast = index === entries.length - 1;
            const isCancelled = String(entry?.action || '')
              .toLowerCase()
              .includes('cancel');
            const iconColors = getHistoryIconColors(entry?.action);

            return (
              <div
                key={entry?.id || `${entry?.createdAt || 'history'}-${index}`}
                style={{
                  position: 'relative',
                  paddingBottom: isLast ? 0 : '24px',
                }}
              >
                {!isLast && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '-29px',
                      top: '30px',
                      bottom: 0,
                      width: '2px',
                      background: '#075dc5',
                    }}
                  />
                )}
                <div
                  style={{
                    position: 'absolute',
                    left: '-42px',
                    top: 0,
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: iconColors.background,
                    color: iconColors.color,
                    border: `2px solid ${iconColors.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  }}
                >
                  {getHistoryIcon(entry?.action)}
                </div>
                <div
                  style={{
                    borderRadius: '8px',
                    border: `1px solid ${isCancelled ? '#ffccc7' : '#e6edf5'}`,
                    background: isCancelled ? '#fffafa' : '#fafcff',
                    padding: '14px 18px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '6px',
                    }}
                  >
                    <Typography.Title
                      level={5}
                      style={{
                        margin: 0,
                        fontSize: '15px',
                        color: isCancelled ? '#cf1322' : '#1a1a1a',
                      }}
                    >
                      {getHistoryActionLabel(entry?.action)} <span style={{}}>| </span>
                      <span style={{ color: '#595959', fontSize: '14px', lineHeight: 1.55 }}>
                        {getHistoryDescription(entry, currency)}
                      </span>
                    </Typography.Title>
                    <span
                      style={{
                        padding: '2px 10px',
                        borderRadius: '12px',
                        background: isCancelled ? '#fff1f0' : '#e6f4ea',
                        color: isCancelled ? '#cf1322' : '#2e7d32',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {isCancelled ? 'Cancelled' : 'Completed'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '6px',
                      color: '#075dc5',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    <span>{displayDate(entry?.createdAt, true)}</span>
                    <span style={{ color: '#bfbfbf' }}>·</span>
                    <span style={{ color: '#60758d', fontWeight: 500 }}>
                      {entry?.performedBy || 'System'}
                    </span>
                  </div>
                  {/* <div style={{ color: '#595959', fontSize: '14px', lineHeight: 1.55 }}>
                    {getHistoryDescription(entry, currency)}
                  </div> */}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const VoucherDetailPanel = ({
  voucher,
  formDetails,
  orderMembers,
  onUpdated,
  showSettlementForm,
  onToggleSettlement,
}: {
  voucher: any;
  formDetails?: any;
  orderMembers: any[];
  onUpdated: (voucher: any) => void;
  showSettlementForm?: boolean;
  onToggleSettlement?: (next: boolean) => void;
}) => {
  const [showWithdraw, setShowWithdraw] = useState(false);
  const isSuperAdmin = getRole() === Role.SUPER_ADMIN;
  const feeTotals = voucher?.feeTotals || {};
  const closed = isClosedVoucherStatus(voucher);

  const handleCancelVoucher = async () => {
    try {
      const voucherKey = getVoucherKey(voucher);
      if (!voucherKey) {
        toastError('Payment voucher is missing');
        return;
      }
      const res = await callApi(`${PAYMENT_VOUCHER}/${voucherKey}/cancel`, 'PUT');
      if (res?.status) {
        toastSuccess('Payment voucher cancelled');
        onUpdated(res?.data?.data);
      } else {
        toastError(res?.data?.message || 'Failed to cancel voucher');
      }
    } catch (error) {
      toastError('Failed to cancel voucher');
    }
  };

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          {
            label: 'Received Amount',
            value: money(voucher.receivedAmount, voucher.currency),
            accent: '#004195',
            bg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',

            border: '#bae6fd',
          },
          {
            label: 'Withdrawn Amount',
            value: money(voucher.withdrawnAmount, voucher.currency),
            accent: '#0f172a',
            bg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            border: '#cbd5e1',
          },
          {
            label: 'Remaining Amount',
            value: money(voucher.remainingAmount, voucher.currency),
            accent: '#15803d',
            bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            border: '#86efac',
          },
          {
            label: 'Total Settled Amount',
            value: money(voucher.settledAmount, voucher.currency),
            accent: '#0f172a',
            bg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            border: '#cbd5e1',
          },
        ].map(item => (
          <Col xs={24} md={6} key={item.label}>
            <Card
              size="small"
              bodyStyle={{
                padding: '14px 16px',
                background: item.bg,
              }}
              style={{
                height: '100%',
                border: `1px solid ${item.border}`,
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  minHeight: 42,
                }}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#475569',
                  }}
                >
                  {item.label}
                </Text>
                <Text
                  strong
                  style={{
                    fontSize: 18,
                    color: item.accent,
                    whiteSpace: 'nowrap',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {item.value}
                </Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 14,
          overflow: 'hidden',
          background: '#ffffff',
          padding: 20,
          marginBottom: 16,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        }}
      >
        <ReceiptsSection voucher={voucher} formDetails={formDetails} onSaved={onUpdated} />

        <div
          style={{
            paddingTop: 20,
            marginTop: 20,
            borderTop: '1px solid #f1f5f9',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 12,
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <Text strong style={{ fontSize: '15px', color: '#004195' }}>
              Member-wise Record Payment
            </Text>
            {!closed && Number(voucher.remainingAmount || 0) > 0.004 && (
              <InvertFillButton
                variant={showWithdraw ? 'red' : 'white'}
                size="sm"
                fontWeight="semibold"
                onClick={() => setShowWithdraw(!showWithdraw)}
              >
                {showWithdraw ? 'Cancel' : '+ Record Payment'}
              </InvertFillButton>
            )}
          </div>
          {!closed && Number(voucher.remainingAmount || 0) <= 0.004 && (
            <Text
              type="secondary"
              style={{
                display: 'block',
                marginBottom: 12,
                padding: '10px 14px',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                color: '#92400e',
                fontSize: '13px',
              }}
            >
              Remaining amount is 0. Record Payment is disabled.
            </Text>
          )}
          {showWithdraw && Number(voucher.remainingAmount || 0) > 0.004 && (
            <AddWithdrawalForm
              voucher={mergeVoucherMembers(voucher, orderMembers)}
              onCancel={() => setShowWithdraw(false)}
              onSaved={updated => {
                onUpdated(updated);
                setShowWithdraw(false);
              }}
            />
          )}
          <div
            style={{
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <MemberWithdrawalTables voucher={mergeVoucherMembers(voucher, orderMembers)} />
          </div>
        </div>

        <div
          style={{
            paddingTop: 20,
            borderTop: '1px solid #f1f5f9',
          }}
        >
          <SettlementSection
            voucher={voucher}
            onSaved={onUpdated}
            open={showSettlementForm}
            onToggle={onToggleSettlement}
          />
        </div>
      </div>

      <div
        style={{
          padding: '16px',
          borderRadius: '8px',
          background: 'rgba(0,48,135,0.03)',
          border: '1px solid rgba(0,48,135,0.1)',
        }}
      >
        <div
          style={{
            marginBottom: '12px',
            fontSize: '15px',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: '#004195',
            paddingBottom: '8px',
            borderBottom: '1px solid rgba(0,48,135,0.15)',
          }}
        >
          Fee Type Summary
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            ...FEE_TYPES.filter(feeType => Number(feeTotals[feeType] || 0) > 0.004).map(
              feeType => ({
                label: feeType,
                value: feeTotals[feeType] || 0,
              })
            ),
            ...(Number(voucher.settledAmount || 0) > 0.004
              ? [{ label: 'Settled Amount', value: voucher.settledAmount }]
              : []),
            { label: 'Remaining Amount', value: voucher.remainingAmount },
          ].map((item, index, array) => (
            <div key={item.label}>
              {index > 0 && (
                <span
                  style={{
                    height: '1px',
                    width: '100%',
                    backgroundColor: '#E5E7EB',
                    display: 'block',
                    marginBottom: '6px',
                  }}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span
                  style={{
                    fontSize: '15px',
                    color: item.label === 'Remaining Amount' ? '#004195' : '#1e293b',
                    fontWeight: item.label === 'Remaining Amount' ? 800 : 600,
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: item.label === 'Remaining Amount' ? 800 : 600,
                    color: item.label === 'Remaining Amount' ? '#15803d' : '#1e293b',
                  }}
                >
                  {money(item.value, voucher.currency)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {!closed && isSuperAdmin && (
        <div style={{ marginTop: 16 }}>
          <Popconfirm
            title="Cancel this payment voucher?"
            onConfirm={handleCancelVoucher}
            okText="Cancel Voucher"
            okButtonProps={{ danger: true }}
          >
            <span>
              <InvertFillButton variant="red" size="sm" fontWeight="semibold">
                Cancel Voucher
              </InvertFillButton>
            </span>
          </Popconfirm>
        </div>
      )}

      <VoucherTrackingHistory history={voucher.history || []} currency={voucher.currency} />
    </>
  );
};

const PaymentVoucher = ({
  formDetails,
  embedded = false,
  refreshTrigger = 0,
}: {
  formDetails?: any;
  embedded?: boolean;
  refreshTrigger?: number;
}) => {
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showSettlementForm, setShowSettlementForm] = useState(false);
  const saveSettlementActionRef = useRef<(() => Promise<void>) | null>(null);
  const orderId = formDetails?.id;
  const orderMembers = normalizeOrderMembers(formDetails?.members || []);

  const selectedVoucher =
    vouchers.find(row => String(row.id) === String(selectedId)) || vouchers[0] || null;
  const hasActiveVoucher = vouchers.some(row => !isClosedVoucherStatus(row));
  const canRecordNewReceipt = !hasActiveVoucher;

  const upsertVoucher = (next: any) => {
    if (!isValidVoucher(next)) return;
    const merged = mergeVoucherMembers(next, orderMembers);
    setVouchers(prev => {
      const key = String(merged.id);
      const without = prev.filter(row => String(row.id) !== key);
      return [merged, ...without].sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime() ||
          Number(b.id) - Number(a.id)
      );
    });
    setSelectedId(String(merged.id));
    setShowSettlementForm(false);
  };

  const fetchVouchers = async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const res = await callApi(`${PAYMENT_VOUCHER}/order/${orderId}`, 'GET');
      if (res?.status) {
        const payload = res?.data?.data;
        const list = Array.isArray(payload?.vouchers)
          ? payload.vouchers
          : payload?.voucher
            ? [payload.voucher]
            : payload && !payload.vouchers
              ? [payload]
              : [];
        const merged = list
          .filter(isValidVoucher)
          .map((row: any) => mergeVoucherMembers(row, orderMembers));
        setVouchers(merged);
        const preferred =
          payload?.activeVoucherId ||
          merged.find((row: any) => !isClosedVoucherStatus(row))?.id ||
          merged[0]?.id ||
          null;
        setSelectedId(preferred ? String(preferred) : null);
      } else {
        toastError(res?.data?.message || 'Failed to load payment vouchers');
      }
    } catch (error) {
      console.error('Error fetching payment vouchers:', error);
      toastError('Failed to load payment vouchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, [orderId, refreshTrigger]);

  useEffect(() => {
    setShowSettlementForm(false);
  }, [selectedId]);

  const renderVoucherSelector = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      {vouchers.map(row => {
        const active = String(row.id) === String(selectedVoucher?.id);
        return (
          <div
            key={String(row.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 6px 4px 4px',
              borderRadius: 999,
              border: active ? '1px solid #1677ff' : '1px solid #d9d9d9',
              background: active ? '#e6f4ff' : '#fff',
              boxShadow: active ? '0 0 0 2px rgba(22, 119, 255, 0.08)' : 'none',
            }}
          >
            <Button
              type={active ? 'primary' : 'default'}
              size="small"
              onClick={() => setSelectedId(String(row.id))}
              style={{
                borderRadius: 999,
                padding: '0 10px',
                height: 28,
                fontWeight: 600,
                border: 'none',
              }}
            >
              {row.voucherId || row.id}
            </Button>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {statusTag(row.status)}
            </span>
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={e => {
                e.stopPropagation();
                downloadVoucherReport(row);
              }}
              aria-label={`Download voucher ${row.voucherId || row.id}`}
            />
          </div>
        );
      })}
    </div>
  );

  const renderHeaderBar = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontSize: '15px',
            fontWeight: 700,
            color: '#004195',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Payment Voucher Information
        </div>
        {!loading && vouchers.length > 0 && renderVoucherSelector()}
      </div>

      {!loading && canRecordNewReceipt && !showCreate && (
        <InvertFillButton
          variant="blue"
          size="sm"
          fontWeight="semibold"
          onClick={() => setShowCreate(true)}
        >
          Record Receipt
        </InvertFillButton>
      )}
    </div>
  );

  const body = (
    <>
      {loading && <Text type="secondary">Loading vouchers...</Text>}

      {!loading && renderHeaderBar()}

      {!loading && showCreate && (
        <div style={{ marginBottom: 16 }}>
          {/* <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {vouchers.length
              ? 'This will create a new payment voucher with a new voucher number for this order.'
              : 'Record a customer receipt to generate the first payment voucher for this order.'}
          </Text> */}
          <CreateVoucherForm
            orderId={orderId}
            formDetails={formDetails}
            onCancel={() => setShowCreate(false)}
            onCreated={created => {
              upsertVoucher(created);
              setShowCreate(false);
            }}
          />
        </div>
      )}

      {!loading && !vouchers.length && !showCreate && (
        <Text type="secondary">No payment vouchers recorded for this order yet.</Text>
      )}

      {!loading && selectedVoucher && !showCreate && (
        <div style={{}}>
          {/* <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 16,
              padding: '14px 16px',
              background: '#f8fafc',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
            }}
          >
            <div>
              <Text strong style={{ fontSize: 16, marginRight: 8, color: '#004195' }}>
                {selectedVoucher.voucherId}
              </Text>
              {statusTag(selectedVoucher.status)}{' '}
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Created {displayDate(selectedVoucher.createdAt, true)}
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {Number(selectedVoucher.remainingAmount || 0) > 0.004 &&
                !selectedVoucher.cancelled && (
                  <>
                    {showSettlementForm && (
                      <InvertFillButton
                        variant="red"
                        size="sm"
                        fontWeight="semibold"
                        onClick={async () => {
                          if (saveSettlementActionRef.current) {
                            await saveSettlementActionRef.current();
                          }
                        }}
                      >
                        Save Settlement
                      </InvertFillButton>
                    )}
                    <InvertFillButton
                      variant={showSettlementForm ? 'white' : 'red'}
                      size="sm"
                      fontWeight="semibold"
                      onClick={() => {
                        if (showSettlementForm) {
                          setShowSettlementForm(false);
                          return;
                        }
                        setShowSettlementForm(true);
                      }}
                    >
                      {showSettlementForm ? 'Cancel' : 'Settle Remaining Amount'}
                    </InvertFillButton>
                  </>
                )}
              <InvertFillButton
                variant="blue"
                size="sm"
                fontWeight="semibold"
                onClick={() => downloadVoucherReport(selectedVoucher)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <DownloadOutlined />
                  Download Voucher Report
                </span>
              </InvertFillButton>
            </div>
          </div> */}
          {showSettlementForm && (
            <div
              style={{
                marginBottom: 16,
                padding: '16px',
                background: '#eff6ff',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #bfdbfe',
              }}
            >
              <SettlementSection
                voucher={selectedVoucher}
                onSaved={upsertVoucher}
                open={true}
                onToggle={setShowSettlementForm}
                showTable={false}
                showActions={false}
                onSetSaveAction={save => {
                  saveSettlementActionRef.current = save;
                }}
              />
            </div>
          )}
          <VoucherDetailPanel
            voucher={selectedVoucher}
            formDetails={formDetails}
            orderMembers={orderMembers}
            onUpdated={upsertVoucher}
          />
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div style={{ marginTop: 8 }}>{body}</div>;
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <Collapse expandIconPosition="right" defaultActiveKey={['1']}>
        <Panel key="1" header="Payment Voucher">
          {body}
        </Panel>
      </Collapse>
    </div>
  );
};

export default PaymentVoucher;
export {
  MemberWithdrawalTables,
  SettlementSection,
  AddWithdrawalForm,
  AddReceiptForm,
  ReceiptsSection,
};
